/**
 * Footer Component
 *
 * Site footer with SZU gray background, organized link sections,
 * and institution information following the SZU design system.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Beaker, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';

/**
 * Footer link type with typed translation keys
 */
type NavKey =
  | 'nav.catalog'
  | 'nav.about'
  | 'nav.ordering'
  | 'nav.shipping'
  | 'nav.contact'
  | 'nav.faq'
  | 'nav.privacy'
  | 'nav.terms';

interface FooterLinkItem {
  to: string;
  labelKey: NavKey;
}

/**
 * Footer component with SZU gray background and organized sections
 */
export function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  /**
   * Footer links organized by section with translation keys
   */
  const footerLinks: {
    collection: FooterLinkItem[];
    support: FooterLinkItem[];
  } = {
    collection: [
      { to: '/catalog', labelKey: 'nav.catalog' },
      { to: '/about', labelKey: 'nav.about' },
      { to: '/ordering', labelKey: 'nav.ordering' },
      { to: '/shipping', labelKey: 'nav.shipping' },
    ],
    support: [
      { to: '/contact', labelKey: 'nav.contact' },
      { to: '/faq', labelKey: 'nav.faq' },
      { to: '/privacy', labelKey: 'nav.privacy' },
      { to: '/terms', labelKey: 'nav.terms' },
    ],
  };

  return (
    <footer className="border-t border-gray-200 bg-gray-100">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-12 lg:px-6">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand and description */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link
              to="/"
              className="inline-flex items-center gap-2 transition-opacity hover:opacity-80"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <Beaker className="h-5 w-5 text-white" aria-hidden="true" />
              </div>
              <span className="text-lg font-bold text-gray-900">{t('header.siteName')}</span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-gray-600">
              {t('footer.institutionFull')}
            </p>
          </div>

          {/* Collection links */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-900">
              {t('footer.sections.collection')}
            </h3>
            <ul className="space-y-3">
              {footerLinks.collection.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-gray-600 transition-colors hover:text-primary"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support links */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-900">
              {t('footer.sections.support')}
            </h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-gray-600 transition-colors hover:text-primary"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact information */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-900">
              {t('footer.sections.contact')}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                <div>
                  <p>{t('footer.address.name')}</p>
                  <p>{t('footer.address.street')}</p>
                  <p>{t('footer.address.city')}</p>
                </div>
              </li>
              <li>
                <a
                  href="mailto:cnctc@szu.cz"
                  className="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-primary"
                >
                  <Mail className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  cnctc@szu.cz
                </a>
              </li>
              <li>
                <a
                  href="tel:+420267082256"
                  className="flex items-center gap-2 text-sm text-gray-600 transition-colors hover:text-primary"
                >
                  <Phone className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  +420 267 082 256
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar with SZU branding */}
      <div className="border-t border-gray-200 bg-gray-50">
        <div className="container mx-auto px-4 py-6 lg:px-6">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            {/* Copyright */}
            <p className="text-sm text-gray-500">{t('footer.copyright', { year: currentYear })}</p>

            {/* SZU link */}
            <a
              href="https://szu.cz"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-primary"
            >
              {t('footer.partOf')}
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
